// components/onboarding/QuickIntro.jsx
// 3-screen swipeable intro - Get users on the flywheel in < 30 seconds
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Users, Coins, Shield, Check } from 'lucide-react';
import { getOnboardingBonusesDisplay, TOKEN_CONFIG } from '../../utils/tokenEconomy';

const QuickIntro = ({ onComplete, referrerName = null }) => {
  const [currentScreen, setCurrentScreen] = useState(0);
  const navigate = useNavigate();
  const bonuses = getOnboardingBonusesDisplay();
  
  const screens = [
    {
      id: 'group-buying',
      icon: Users,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Buy Together, Save Together',
      subtitle: 'The more people join, the more everyone saves',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">Retail Price</span>
              <span className="text-lg line-through text-gray-400">$50.00</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-xs font-bold text-blue-700">5</div>
                  <span className="ml-2 text-sm text-gray-600">people join</span>
                </div>
                <span className="font-semibold text-green-600">$42.50 <span className="text-xs text-gray-500">(15% off)</span></span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-blue-300 flex items-center justify-center text-xs font-bold text-blue-700">15</div>
                  <span className="ml-2 text-sm text-gray-600">people join</span>
                </div>
                <span className="font-semibold text-green-600">$37.50 <span className="text-xs text-gray-500">(25% off)</span></span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">50+</div>
                  <span className="ml-2 text-sm text-gray-600">people join</span>
                </div>
                <span className="font-bold text-green-700 text-lg">$30.00 <span className="text-xs text-gray-500">(40% off)</span></span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600">
            Shop with your community and unlock bulk pricing normally reserved for businesses.
          </p>
        </div>
      )
    },
    {
      id: 'token-economy',
      icon: Coins,
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      title: 'Earn Tokens Every Step',
      subtitle: '1 token = $0.10 platform credit',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-4 h-4 text-green-600" />
                  </div>
                  <span className="ml-2 text-sm">Sign up today</span>
                </div>
                <span className="font-bold text-yellow-600">+{bonuses.welcome.tokens} tokens</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <span className="ml-2 text-sm">Invite friends</span>
                </div>
                <span className="font-bold text-yellow-600">+{bonuses.referral.sharerTokens} tokens each</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                    <Coins className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="ml-2 text-sm">Complete purchases</span>
                </div>
                <span className="font-bold text-yellow-600">+10-50 tokens</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center">
                  <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                    <span className="text-sm">🚶</span>
                  </div>
                  <span className="ml-2 text-sm">Help with local deliveries</span>
                </div>
                <span className="font-bold text-yellow-600">+25-80 tokens</span>
              </div>
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Use tokens for <span className="font-semibold text-yellow-700">up to 40% off</span> your orders
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Tokens expire in {TOKEN_CONFIG.TOKEN_EXPIRATION_DAYS} days - spend them on what you need!
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'trust-score',
      icon: Shield,
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      title: 'Build Your Reputation',
      subtitle: 'Higher trust = better opportunities',
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-4">
            {/* Trust Score Visual */}
            <div className="flex items-center justify-center mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full border-4 border-green-200 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">3.0</div>
                    <div className="text-xs text-gray-500">Starting</div>
                  </div>
                </div>
                <div className="absolute -right-2 -top-2 bg-yellow-400 rounded-full p-1">
                  <span className="text-xs">⭐</span>
                </div>
              </div>
            </div>
            
            {/* How to Build Trust */}
            <div className="space-y-2">
              <div className="flex items-center text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Complete purchases</span>
                <span className="ml-auto text-green-600 font-medium">+0.1</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Verify your skills</span>
                <span className="ml-auto text-green-600 font-medium">+0.2</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Complete local jobs</span>
                <span className="ml-auto text-green-600 font-medium">+0.3</span>
              </div>
              <div className="flex items-center text-sm">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700">Get 5-star ratings</span>
                <span className="ml-auto text-green-600 font-medium">+0.2</span>
              </div>
            </div>
          </div>
          
          <div className="text-center text-sm text-gray-600">
            <p>Reach <span className="font-semibold text-green-700">4.0+</span> to unlock premium job opportunities</p>
          </div>
        </div>
      )
    }
  ];
  
  const currentScreenData = screens[currentScreen];
  const isLastScreen = currentScreen === screens.length - 1;
  const isFirstScreen = currentScreen === 0;
  
  const handleNext = () => {
    if (isLastScreen) {
      // Mark intro as seen
      localStorage.setItem('quick_intro_completed', 'true');
      
      // Trigger AVA to offer tours after a short delay
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('ava-open', {
          detail: {
            openTab: 'tours',
            prompt: "Welcome! Would you like me to give you a quick tour of the platform? You'll earn tokens for each tour you complete! 🎉"
          }
        }));
      }, 1500);

      if (onComplete) {
        onComplete();
      } else {
        navigate('/home');
      }
    } else {
      setCurrentScreen(prev => prev + 1);
    }
  };
  
  const handlePrev = () => {
    if (!isFirstScreen) {
      setCurrentScreen(prev => prev - 1);
    }
  };
  
  const handleSkip = () => {
    localStorage.setItem('quick_intro_completed', 'true');
    if (onComplete) {
      onComplete();
    } else {
      navigate('/home');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 flex flex-col">
      {/* Header */}
      <div className="p-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          {currentScreen + 1} of {screens.length}
        </div>
        <button 
          onClick={handleSkip}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Skip
        </button>
      </div>
      
      {/* Referral Banner (if applicable) */}
      {referrerName && currentScreen === 0 && (
        <div className="mx-4 mb-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg p-3 text-center">
          <p className="text-sm">
            <span className="font-semibold">{referrerName}</span> invited you!
          </p>
          <p className="text-xs opacity-90">
            You both get {bonuses.referral.sharerValue} in tokens when you join
          </p>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 px-6 py-4">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className={`w-20 h-20 rounded-full ${currentScreenData.iconBg} flex items-center justify-center`}>
            <currentScreenData.icon className={`w-10 h-10 ${currentScreenData.iconColor}`} />
          </div>
        </div>
        
        {/* Title */}
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">
          {currentScreenData.title}
        </h1>
        
        {/* Subtitle */}
        <p className="text-center text-gray-600 mb-6">
          {currentScreenData.subtitle}
        </p>
        
        {/* Content */}
        <div className="max-w-sm mx-auto">
          {currentScreenData.content}
        </div>
      </div>
      
      {/* Progress Dots */}
      <div className="flex justify-center space-x-2 py-4">
        {screens.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentScreen(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              currentScreen === index 
                ? 'w-8 bg-blue-600' 
                : 'w-2 bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
      
      {/* Navigation Buttons */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={handlePrev}
          className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
            isFirstScreen 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          disabled={isFirstScreen}
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back
        </button>
        
        <button
          onClick={handleNext}
          className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-lg"
        >
          {isLastScreen ? (
            <>
              Get Started
              <Check className="w-5 h-5 ml-2" />
            </>
          ) : (
            <>
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </>
          )}
        </button>
      </div>
      
      {/* Welcome Bonus Teaser (on last screen) */}
      {isLastScreen && (
        <div className="px-4 pb-6">
          <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-3 text-center">
            <p className="text-sm font-medium text-yellow-800">
              🎉 You'll receive <span className="font-bold">{bonuses.welcome.tokens} tokens ({bonuses.welcome.value})</span> when you finish signing up!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickIntro;